import { useEffect, useState } from "react";
import styles from "./list.module.css";
import { TextField, Button, Tooltip } from "@mui/material";
import TablePaginationComponent from "../../components/TablePagination";
import { Close } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import Loader from "../../components/Loader";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Link } from "react-router-dom";
import download from "downloadjs";
import { useReactToPrint } from "react-to-print";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { File, FileText, Search } from "react-feather";
import { useRef } from "react";
import { useCallback } from "react";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import PageHeader from "../../components/PageHeader";
import TableLoader from "./ContentLoader";

/**
 * Single source of truth for status -> label/color, shared by the legend,
 * the desktop row accent, and the mobile card chip.
 */
const STATUS_META = {
  0: { label: "Not shipped", color: "var(--status-none)", bg: "var(--status-none-bg)" },
  1: { label: "Shipped from warehouse", color: "var(--status-shipped)", bg: "var(--status-shipped-bg)" },
  2: { label: "Received in Ghana", color: "var(--status-received)", bg: "var(--status-received-bg)" },
  3: { label: "Delivered", color: "var(--status-delivered)", bg: "var(--status-delivered-bg)" },
};

const getStatusMeta = (item) => {
  const key = Number(item?.status);
  return STATUS_META[key] ?? STATUS_META[0];
};

const isEditable = (item, auth) => {
  const status = Number(item?.status);
  const canEdit = auth?.user_type === 2 || auth?.permissions?.indexOf("shipping:update") > -1;
  return status < 2 && canEdit;
};

const StatusChip = ({ status }) => {
  const meta = getStatusMeta({ status });
  return (
    <span className={styles.StatusChip} style={{ color: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
};

const RouteLine = ({ from, to }) => (
  <div className={styles.RouteLine}>
    <span>{from}</span>
    <div className={styles.RouteTrack} />
    <span>{to}</span>
  </div>
);

const ButtonArea = ({ data }) => {
  const componentRef = useRef();

  const Export = ({ onExport }) => (<Button onClick={(e) => onExport(e.target.value)} className={`${styles.TableAddBU}`} style={{ width: "auto" }}>Export To CSV</Button>);

  const convertArrayOfObjectsToCSV = (array) => {
    let result = "";

    const columnDelimiter = ",";
    const lineDelimiter = "\n";
    const keys = ["reference_id", "container_name", "freight", "shipping_from", "shipping_to", "shipping_date", "arrival_date", "consignee", "no_of_invoice", "warehouse_location"];

    keys.forEach((key) => {
      if (key === "reference_id") {
        result += "Shipping Ref";
      } else if (key === "container_name") {
        result += "Container No";
      } else if (key === "freight") {
        result += "Freight Name";
      } else if (key === "shipping_from") {
        result += "Shipping From";
      } else if (key === "shipping_to") {
        result += "Shipping To";
      } else if (key === "shipping_date") {
        result += "Shipping Date";
      } else if (key === "arrival_date") {
        result += "Arrival Date";
      } else if (key === "consignee") {
        result += "Consignee";
      } else if (key === "no_of_invoice") {
        result += "Invoice Count";
      } else if (key === "warehouse_location") {
        result += "Warehouse";
      } else {
        result += key;
      }
      result += columnDelimiter;
    });
    result += lineDelimiter;

    array.forEach((item) => {
      let ctr = 0;
      keys.forEach((key) => {
        if (ctr > 0) result += columnDelimiter;
        result += item[key];
        ctr++;
      });
      result += lineDelimiter;
    });

    return result;
  };

  const downloadCSV = useCallback(() => {
    let newArray = data.map((row) => {
      return {
        reference_id: row.reference_id,
        container_name: row.container_name,
        freight: row.freight,
        shipping_from: row.shipping_from,
        shipping_to: row.shipping_to,
        shipping_date: row.shipping_date ? dayjs(row.shipping_date).format("DD/MM/YYYY") : "-",
        arrival_date: row.arrival_date ? dayjs(row.arrival_date).format("DD/MM/YYYY") : "-",
        consignee: row.consignee,
        no_of_invoice: row.no_of_invoice,
        warehouse_location: row.warehouse_location,
      };
    });

    const link = document.createElement("a");
    let csv = convertArrayOfObjectsToCSV(newArray);
    if (csv == null) return;

    const filename = "shipping-list.csv";

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", filename);
    link.click();
  }, [data]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (<>
    <Export onExport={() => downloadCSV()} />
    <Button onClick={handlePrint} className={`${styles.TableAddBU}`} style={{ width: "auto" }}>Print Shippings</Button>
    <div style={{ display: "none" }}>
      <ComponentToPrintList ref={componentRef} data={data} />
    </div>
  </>);
};

const ShippingList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterVars, setFilterVars] = useState({ searchKey: "" });

  const { websiteData } = useWebsite();
  useSiteBranding("Shipping List", websiteData);

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchData = () => {
      setContentLoading(true);
      axios.get(process.env.REACT_APP_API_URL + "v1/shippings", {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setContentLoading(false);
        setAllData(response.data.data);
      }).catch((error) => {
        setContentLoading(false);
      });
    };

    fetchData();
  }, [API_TOKEN]);

  useEffect(() => {
    let filterredTempData = allData;

    if (filterVars?.searchKey !== "") {
      let inputVal = filterVars?.searchKey;
      inputVal = inputVal.toLowerCase();
      filterredTempData = filterredTempData.filter((item) => {
        return (item?.reference_id.toLowerCase().includes(inputVal) || item?.container_name.toLowerCase().includes(inputVal) || item?.shipping_from.toLowerCase().includes(inputVal) || item?.shipping_to.toLowerCase().includes(inputVal) || item?.consignee.toLowerCase().includes(inputVal) || item?.warehouse_location.toLowerCase().includes(inputVal));
      });
    }

    setFilteredData(filterredTempData);
  }, [allData, filterVars]);

  const convertArrayOfObjectsToCSV2 = (array) => {
    let result = "";

    const columnDelimiter = ",";
    const lineDelimiter = "\n";
    const keys = ["container_id", "booking_id", "item_id", "title", "amount", "recipient_name", "recipient_contact", "recipient_location"];

    keys.forEach((key) => {
      if (key === "container_id") {
        result += "Container Id";
      } else if (key === "booking_id") {
        result += "Booking Id";
      } else if (key === "item_id") {
        result += "Pickup Id";
      } else if (key === "title") {
        result += "Product";
      } else if (key === "amount") {
        result += "Cost";
      } else if (key === "recipient_name") {
        result += "Recipient Name";
      } else if (key === "recipient_contact") {
        result += "Recipient Contact";
      } else if (key === "recipient_location") {
        result += "Recipient Location";
      } else {
        result += key;
      }
      result += columnDelimiter;
    });
    result += lineDelimiter;

    array.forEach((item) => {
      let ctr = 0;
      keys.forEach((key) => {
        if (ctr > 0) result += columnDelimiter;
        result += item[key];
        ctr++;
      });
      result += lineDelimiter;
    });

    return result;
  };

  const downloadItemCSV = (shipId) => {
    axios.get(process.env.REACT_APP_API_URL + "v1/shippings/booking-list/" + shipId).then((response) => {
      if (response.data.status === "SUCCESS") {
        let newArray = response.data.data;
        const link = document.createElement("a");
        let csv = convertArrayOfObjectsToCSV2(newArray);
        if (csv == null) return;
        const filename = "shipping-item-list.csv";

        if (!csv.match(/^data:text\/csv/i)) {
          csv = `data:text/csv;charset=utf-8,${csv}`;
        }
        link.setAttribute("href", encodeURI(csv));
        link.setAttribute("download", filename);
        link.click();
      }
    });
  };

  const downloadItemPdf = (shipId) => {
    axios(process.env.REACT_APP_API_URL + "v1/shippings-item-pdf/" + shipId, {
      responseType: "blob",
    }).then((response) => {
      const content = response.headers["content-type"];
      download(response.data, "itemdownload.pdf", content);
    });
  };

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.MainDiv}`}>
      <PageHeader title="Shipping" trail={[{ label: 'List' }]} />
      <div className={`${styles.MainCard}`}>
        {contentLoading && <TableLoader rows={rowsPerPage > 8 ? 8 : rowsPerPage} columns={8} cards={4} />}

        {!contentLoading && (<>
          <div className={`${styles.TableFilterHead}`}>
            <div className={`${styles.SearchWrap}`}>
              <Search className={`${styles.SearchIcon}`} />
              <TextField
                variant="standard"
                type="text"
                placeholder="Search input"
                value={filterVars?.searchKey}
                onChange={(e) => setFilterVars((prev) => ({ ...prev, searchKey: e.target.value }))}
                className={styles.SearchInput}
                InputProps={{ disableUnderline: true }}
                fullWidth
              />
              {filterVars?.searchKey !== "" && (<Button type="button" className={`${styles.CloseBU}`} onClick={(e) => setFilterVars((prev) => { return { ...prev, searchKey: "" }; })}><Close /></Button>)}
            </div>
            <div className={`${styles.TableFilterBUSec}`}>
              <ButtonArea data={filteredData}></ButtonArea>
            </div>
          </div>

          <div className={`${styles.CoutryTagSec}`}>
            {Object.values(STATUS_META).map((meta) => (
              <div className={`${styles.CoutryTag}`} key={meta.label}>
                <span className={`${styles.TagColor}`} style={{ background: meta.color }}></span>
                <span>{meta.label}</span>
              </div>
            ))}
          </div>

          {/* ============ Desktop table (>=880px) ============ */}
          <div className={styles.DesktopTableWrap}>
            <div className={styles.TableWrap}>
              <div className={styles.TableScroll}>
                <div className={styles.TableGrid} role="table" aria-label="Shipments">
                  <div className={styles.TableGridHeadRow} role="row">
                    <div role="columnheader">Shipment</div>
                    <div role="columnheader">Route</div>
                    <div role="columnheader">Freight</div>
                    <div role="columnheader">Timeline</div>
                    <div role="columnheader">Consignee</div>
                    <div role="columnheader">Warehouse</div>
                    <div role="columnheader">Status</div>
                    <div role="columnheader" style={{ textAlign: "right" }}>Actions</div>
                  </div>

                  {paginatedData.map((item, index) => {
                    const meta = getStatusMeta(item);
                    const rowStyle = { "--row-accent": meta.color, "--row-bg": meta.bg };
                    return (
                      <div className={styles.TableGridRow} role="row" key={index} style={rowStyle}>
                        <div role="cell">
                          <div className={styles.ShipmentCell}>
                            <span className={styles.ShipmentRef}>{item?.reference_id}</span>
                            <span className={styles.ShipmentContainer}>
                              {isEditable(item, auth)
                                ? (<Link className={styles.ActionLink} to={"/shipping/view/" + item?.id}>{item?.container_name}</Link>)
                                : item?.container_name}
                            </span>
                          </div>
                        </div>
                        <div role="cell">
                          <RouteLine from={item?.shipping_from} to={item?.shipping_to} />
                        </div>
                        <div role="cell">{item?.freight}</div>
                        <div role="cell">
                          <div className={styles.TimelineCell}>
                            <div className={styles.TimelineRow}>
                              <span className={styles.TimelineLabel}>Shipped</span>
                              <span className={styles.TimelineValue}>{item?.shipping_date ? dayjs(item?.shipping_date).format("DD/MM/YYYY") : "-"}</span>
                            </div>
                            <div className={styles.TimelineRow}>
                              <span className={styles.TimelineLabel}>Arrival</span>
                              <span className={styles.TimelineValue}>{item?.arrival_date ? dayjs(item?.arrival_date).format("DD/MM/YYYY") : "-"}</span>
                            </div>
                          </div>
                        </div>
                        <div role="cell">{item?.consignee}</div>
                        <div role="cell">
                          <div className={styles.WarehouseCell}>
                            <span>{item?.warehouse_location}</span>
                            <span className={styles.WarehouseInvoices}>{item?.no_of_invoice} invoice{Number(item?.no_of_invoice) === 1 ? "" : "s"}</span>
                          </div>
                        </div>
                        <div role="cell">
                          <StatusChip status={item?.status} />
                        </div>
                        <div role="cell">
                          <div className={styles.ActionsCell}>
                            <Tooltip title="Download Item List As EXCEL">
                              <Button onClick={(e) => { downloadItemCSV(item?.id); }} className={`${styles.TableActionBU}`}><File /></Button>
                            </Tooltip>
                            <Tooltip title="Download Item List As PDF">
                              <Button onClick={(e) => { downloadItemPdf(item?.id); }} className={`${styles.TableActionBU}`}>
                                <FileText />
                              </Button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {filteredData.length === 0 && (
                    <div className={styles.EmptyRow}>No data found</div>
                  )}
                </div>
              </div>
            </div>

            {filteredData.length > 0 && (<div className={styles.PaginationWrap}>
              <TablePaginationComponent
                count={filteredData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={paginationChangePage}
                handleChangeRowsPerPage={paginationChangeRowsPerPage}
              />
            </div>)}
          </div>

          {/* ============ Mobile cards (<880px) ============ */}
          <div className={styles.MobileCardList}>
            {paginatedData.map((item, index) => {
              const meta = getStatusMeta(item);
              return (
                <div className={styles.MobileCard} key={index} style={{ "--row-accent": meta.color }}>
                  <div className={styles.MobileCardTop}>
                    <div>
                      <p className={styles.MobileCardRef}>{item?.reference_id}</p>
                      <div className={styles.MobileCardContainer}>
                        {isEditable(item, auth)
                          ? (<Link className={styles.ActionLink} to={"/shipping/view/" + item?.id}>{item?.container_name}</Link>)
                          : item?.container_name}
                      </div>
                    </div>
                    <StatusChip status={item?.status} />
                  </div>

                  <div className={styles.MobileCardRoute}>
                    <RouteLine from={item?.shipping_from} to={item?.shipping_to} />
                  </div>

                  <div className={styles.MobileCardMeta}>
                    <div className={styles.MobileCardMetaItem}>
                      <span className={styles.MobileCardMetaLabel}>Shipping date</span>
                      <span className={styles.MobileCardMetaValue}>{item?.shipping_date ? dayjs(item?.shipping_date).format("DD/MM/YYYY") : "-"}</span>
                    </div>
                    <div className={styles.MobileCardMetaItem}>
                      <span className={styles.MobileCardMetaLabel}>Arrival date</span>
                      <span className={styles.MobileCardMetaValue}>{item?.arrival_date ? dayjs(item?.arrival_date).format("DD/MM/YYYY") : "-"}</span>
                    </div>
                    <div className={styles.MobileCardMetaItem}>
                      <span className={styles.MobileCardMetaLabel}>Consignee</span>
                      <span className={styles.MobileCardMetaValue}>{item?.consignee}</span>
                    </div>
                    <div className={styles.MobileCardMetaItem}>
                      <span className={styles.MobileCardMetaLabel}>Warehouse</span>
                      <span className={styles.MobileCardMetaValue}>{item?.warehouse_location} · {item?.no_of_invoice} inv.</span>
                    </div>
                  </div>

                  <div className={styles.MobileCardActions}>
                    <button type="button" className={styles.MobileCardActionBU} onClick={() => downloadItemCSV(item?.id)}>
                      <File /> Excel
                    </button>
                    <button type="button" className={styles.MobileCardActionBU} onClick={() => downloadItemPdf(item?.id)}>
                      <FileText /> PDF
                    </button>
                  </div>
                </div>
              );
            })}

            {filteredData.length === 0 && (<div className={styles.EmptyState}>No data found</div>)}

            {filteredData.length > 0 && (<div className={styles.PaginationWrap}>
              <TablePaginationComponent
                count={filteredData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={paginationChangePage}
                handleChangeRowsPerPage={paginationChangeRowsPerPage}
              />
            </div>)}
          </div>
        </>)}
      </div>
    </div>
  </>);
}

export default withAdminAuth(ShippingList);